import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';

import { EditIcon } from 'src/app/components/icons/edit-icon';
import { FormTextField } from 'src/app/components/lib/form-text-field';
import { IconButton } from 'src/app/components/lib/icon-button';
import { Wizard } from 'src/app/components/lib/wizard';
import { useGetCampaignTemplateQuery } from 'src/app/queries/marketing/campaign-templates/get-campaign-template';
import { marketingTemplatePresetsQueryKey } from 'src/app/queries/marketing/campaign-templates/get-campaign-templates';
import { useSaveCampaignTemplateMutation } from 'src/app/queries/marketing/campaign-templates/upsert-campaign-template';
import { useNotificationActions } from 'src/app/state/notifications';

import { formatTimeAgo } from '../../../campaigns/marketing-campaign-builder/helpers';
import { initialWizardStateAtom } from '../slides/atoms/initial-wizard-state-atom';
import { CampaignTemplateBeeFreeEditor } from '../slides/beefree-editor';
import { CampaignTemplateContentReview } from '../slides/content-review';
import { MarketingTemplateSelect } from '../slides/template-select/marketing-template-select';

import { useTemplateBuilderContext } from './builder-context';
import { getCampaignTemplatesPayload } from './helpers';

import type { ApiErrorResponse } from 'src/app/queries/marketing/upsert-campaign';

const workflowStepsCreate = [
  {
    title: 'Template select',
    Component: MarketingTemplateSelect,
    hideBackButton: true,
    shouldFill: true,
  },
  {
    title: 'Editor',
    Component: CampaignTemplateBeeFreeEditor,
    shouldFill: true,
  },
  {
    title: 'Review',
    Component: CampaignTemplateContentReview,
    shouldFill: true,
  },
];

const workflowStepsEdit = [
  {
    title: 'Editor',
    Component: CampaignTemplateBeeFreeEditor,
    shouldFill: true,
    hideBackButton: true,
  },
  {
    title: 'Review',
    Component: CampaignTemplateContentReview,
    shouldFill: true,
  },
];

type CampaignTemplateWizardProps = {
  onClose: () => void;
  open: boolean;
};

export function CampaignTemplateWizard({ open, onClose }: CampaignTemplateWizardProps) {
  const notifications = useNotificationActions();
  const queryClient = useQueryClient();
  const params = useParams()?.['*'];
  const isEdit = useMemo(() => params?.includes('edit'), [params]);
  const templateIdFromURL = !isEdit ? null : params?.split('/').pop();

  // state
  const [builderState, dispatch] = useTemplateBuilderContext();
  const { templateName, lastModifiedDateUtc } = builderState;

  // Initial wizard state - captures whether this was originally a new template or editing existing
  const [initialWizardState, setInitialWizardState] = useRecoilState(initialWizardStateAtom);

  // queries
  const { data: campaignTemplateDetails, isLoading: campaignTemplateDetailsLoading } = useGetCampaignTemplateQuery({
    marketingTemplatePresetId: parseInt(templateIdFromURL ?? '', 10),
    enabled: isEdit && !!templateIdFromURL,
  });

  const { mutateAsync: saveCampaignTemplate, isLoading: isSaveCampaignTemplateLoading } =
    useSaveCampaignTemplateMutation();

  useEffect(() => {
    if (campaignTemplateDetails) {
      dispatch({ type: 'update-full-template', payload: campaignTemplateDetails });
    }
  }, [campaignTemplateDetails, dispatch]);

  const handleTemplateNameChange = (newName: string) => {
    dispatch({ type: 'set-value', payload: { key: 'templateName', value: newName } });
  };

  // Set initial wizard state when wizard opens
  useEffect(() => {
    if (open && !initialWizardState.isSet) {
      const isNewTemplate = !isEdit || !templateIdFromURL;
      setInitialWizardState({
        isNewTemplate,
        isSet: true,
      });
    }
  }, [open, initialWizardState.isSet, isEdit, templateIdFromURL, setInitialWizardState]);

  // Reset initial state when wizard closes
  useEffect(() => {
    if (!open) {
      setInitialWizardState({
        isNewTemplate: false,
        isSet: false,
      });
    }
  }, [open, onClose, setInitialWizardState]);

  const handleClose = useCallback(async () => {
    if (!isEdit) {
      onClose();

      setInitialWizardState({
        isNewTemplate: false,
        isSet: false,
      });
      return;
    }

    try {
      await saveCampaignTemplate(getCampaignTemplatesPayload({}, builderState));
      onClose();

      notifications.success({
        message: 'Template saved',
        subContent: 'Your template has been saved successfully.',
      });
      // Invalidate the template list query to refresh the data
      await queryClient.invalidateQueries({ queryKey: marketingTemplatePresetsQueryKey });

      setInitialWizardState({
        isNewTemplate: false,
        isSet: false,
      });
    } catch (e) {
      const error = e as ApiErrorResponse;
      notifications.error({
        message: error?.response?.data?.Message ?? 'Error saving template',
      });
    }
  }, [isEdit, onClose, saveCampaignTemplate, builderState, queryClient, notifications, setInitialWizardState]);

  const useMemoWorkflowSteps = useMemo(
    () => (initialWizardState.isNewTemplate ? workflowStepsCreate : workflowStepsEdit),
    [initialWizardState.isNewTemplate]
  );

  const getTitle = () => {
    if (isEdit) {
      return (
        <TitleContainer>
          <EditableCampaignName campaignName={templateName} onSave={handleTemplateNameChange} />
        </TitleContainer>
      );
    }
    return 'Create template';
  };

  const isLoading = () => {
    if (isEdit) {
      return campaignTemplateDetailsLoading || isSaveCampaignTemplateLoading;
    }
    return isSaveCampaignTemplateLoading;
  };

  return (
    <Wizard
      completeButtonDisabled={isLoading()}
      completeButtonLabel='Save template'
      drawerAriaLabel='Create template wizard'
      exitButtonLabel={isEdit ? 'Save & exit' : 'Exit'}
      loading={isLoading()}
      open={open}
      steps={useMemoWorkflowSteps}
      subtitle={<Subtitle isLoading={campaignTemplateDetailsLoading} lastSavedAt={lastModifiedDateUtc} />}
      title={getTitle()}
      titleFontSize='var(--font-large-16pt-semibold)'
      titleMinWidth='300px'
      onClose={handleClose}
      onCompleteButtonClick={async () => {
        await handleClose();
      }}
    />
  );
}

function Subtitle({ lastSavedAt, isLoading }) {
  const [formattedLastSavedAt, setFormattedLastSavedAt] = useState<string>(formatTimeAgo(lastSavedAt));

  // recheck the formatted string after 16 sec (to handle update from "a few seconds ago" to the full timestamp)
  useEffect(() => {
    setFormattedLastSavedAt(formatTimeAgo(lastSavedAt));

    const timeoutID = setTimeout(() => {
      setFormattedLastSavedAt(formatTimeAgo(lastSavedAt));
    }, 16000);

    return () => {
      clearTimeout(timeoutID);
    };
  }, [lastSavedAt]);

  const shouldShowTimestamp = !isLoading && lastSavedAt;
  const noTimestampText = isLoading ? '--' : 'Unsaved';

  return (
    <div>
      {shouldShowTimestamp ? (
        <>
          <StyledLightType>Last saved: </StyledLightType>
          <StyledDarkerType>{formattedLastSavedAt}</StyledDarkerType>
        </>
      ) : (
        <StyledDarkerType>{noTimestampText}</StyledDarkerType>
      )}
    </div>
  );
}

const StyledLightType = styled.span`
  font: var(--font-extra-small-12pt-normal);
  color: var(--color-gray-80);
`;
const StyledDarkerType = styled.span`
  font: var(--font-extra-small-12pt-normal);
  color: var(--color-gray-90);
`;

function EditableCampaignName({ campaignName, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(campaignName);

  useEffect(() => {
    setEditedName(campaignName);
  }, [campaignName]);

  const handleSave = () => {
    if (editedName.trim() !== '') {
      onSave(editedName);
    } else {
      setEditedName(campaignName);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedName(campaignName);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <InputWrapper>
        <FormTextField
          autoFocus
          gridColumns={1}
          placeholder='Enter template name'
          value={editedName}
          onBlur={handleSave}
          onChange={({ target }) => setEditedName(target.value)}
          onKeyDown={handleKeyDown}
        />
      </InputWrapper>
    );
  }

  return (
    <NameContainer>
      <NameText>{campaignName}</NameText>
      <StyledIconButton aria-label='Edit campaign name' onClick={() => setIsEditing(true)}>
        <EditIcon color='var(--color-brand-secondary-sapphire)' fontSize='small' htmlColor='white' />
      </StyledIconButton>
    </NameContainer>
  );
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InputWrapper = styled.div`
  width: 100%;
`;

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sizes-5);
`;

const NameText = styled.span`
  margin-right: var(--sizes-5);
  max-width: 325px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const StyledIconButton = styled(IconButton)`
  padding: 0 0 9px 9px;
`;
